import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay, qtyDisplay } from "@icraftx/shared/v1";
import { Input } from "@/components/ui/input";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { ReturnRow } from "@/shared/api/returns";
import type { SalesInvoiceListRow } from "@/shared/api/sales";

export type SoDetailLine = {
  id: string;
  productId: string;
  productName: string;
  description: string | null;
  qtyOrdered: string;
  unitPrice: string;
  discountPct: string;
  lineGross: string;
  qtyDelivered: string;
  qtyOpen: string;
  trackLot?: boolean;
  trackSerial?: boolean;
};

export type SoDetailDelivery = {
  id: string;
  number: string;
  lifecycle: string;
  postedAt: string | null;
};

export type SoDetailPayment = {
  id: string;
  number: string;
  direction: string;
  lifecycle: string;
  amount: string;
  postedAt: string | null;
};

const lineHelper = createColumnHelper<DeskTableFeatures, SoDetailLine>();
const delivHelper = createColumnHelper<DeskTableFeatures, SoDetailDelivery>();
const invHelper = createColumnHelper<DeskTableFeatures, SalesInvoiceListRow>();
const payHelper = createColumnHelper<DeskTableFeatures, SoDetailPayment>();
const retHelper = createColumnHelper<DeskTableFeatures, ReturnRow>();

export function soLineColumns(opts: {
  canDeliver: boolean;
  delivQty: Record<string, string>;
  setDelivQty: (next: Record<string, string>) => void;
  delivLot: Record<string, string>;
  setDelivLot: (next: Record<string, string>) => void;
  delivSerial: Record<string, string>;
  setDelivSerial: (next: Record<string, string>) => void;
}) {
  const cols = [
    lineHelper.accessor("productName", {
      id: "productName",
      header: "Product",
      size: 200,
    }),
    lineHelper.accessor("qtyOrdered", {
      id: "qtyOrdered",
      header: "Ordered",
      size: 88,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("unitPrice", {
      id: "unitPrice",
      header: "Price",
      size: 96,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("qtyDelivered", {
      id: "qtyDelivered",
      header: "Delivered",
      size: 96,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("qtyOpen", {
      id: "qtyOpen",
      header: "Open",
      size: 80,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("lineGross", {
      id: "lineGross",
      header: "Gross",
      size: 100,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
  ];

  if (!opts.canDeliver) return lineHelper.columns(cols);

  return lineHelper.columns([
    ...cols,
    lineHelper.display({
      id: "deliverQty",
      header: "Deliver",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="w-24 font-mono"
            value={opts.delivQty[l.id] ?? ""}
            onChange={(e) =>
              opts.setDelivQty({ ...opts.delivQty, [l.id]: e.target.value })
            }
          />
        );
      },
    }),
    lineHelper.display({
      id: "lot",
      header: "Lot",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (!l.trackLot || Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="min-w-0 w-full font-mono"
            value={opts.delivLot[l.id] ?? ""}
            onChange={(e) =>
              opts.setDelivLot({ ...opts.delivLot, [l.id]: e.target.value })
            }
            placeholder="Required"
          />
        );
      },
    }),
    lineHelper.display({
      id: "serial",
      header: "Serial",
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const l = row.original;
        if (!l.trackSerial || Number(l.qtyOpen) <= 0) {
          return <span className="text-[var(--color-muted)]">—</span>;
        }
        return (
          <Input
            className="min-w-0 w-full font-mono"
            value={opts.delivSerial[l.id] ?? ""}
            onChange={(e) =>
              opts.setDelivSerial({
                ...opts.delivSerial,
                [l.id]: e.target.value,
              })
            }
            placeholder="Qty 1"
          />
        );
      },
    }),
  ]);
}

export function soDeliveryColumns(companySlug: string) {
  return delivHelper.columns([
    delivHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/sales/deliveries/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    delivHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/deliveries/${info.row.original.id}`}
        />
      ),
    }),
    delivHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export function soInvoiceColumns(companySlug: string) {
  return invHelper.columns([
    invHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/sales/invoices/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    invHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/invoices/${info.row.original.id}`}
        />
      ),
    }),
    invHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Gross",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    invHelper.accessor("docDate", {
      id: "docDate",
      header: "Date",
      size: 120,
      cell: (info) => info.getValue() || "—",
    }),
  ]);
}

export function soPaymentColumns(companySlug: string) {
  return payHelper.columns([
    payHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/sales/payments/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    payHelper.accessor("direction", {
      id: "direction",
      header: "Direction",
      size: 100,
      cell: (info) =>
        info.getValue() === "to_customer" ? "refund" : "collect",
    }),
    payHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/sales/payments/${info.row.original.id}`}
        />
      ),
    }),
    payHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    payHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export function soReturnColumns(companySlug: string) {
  return retHelper.columns([
    retHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <DocNumberLink
          to={`/c/${companySlug}/returns/${info.row.original.id}`}
        >
          {info.getValue()}
        </DocNumberLink>
      ),
    }),
    retHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/returns/${info.row.original.id}`}
        />
      ),
    }),
    retHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Gross",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    retHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export type DeliveryLineRow = {
  id: string;
  productId: string;
  productName: string;
  salesOrderLineId: string;
  qty: string;
  unitCost: string;
  lotCode: string | null;
  serialCode: string | null;
};

const deliveryLineHelper =
  createColumnHelper<DeskTableFeatures, DeliveryLineRow>();

export function salesDeliveryLineColumns() {
  return deliveryLineHelper.columns([
    deliveryLineHelper.accessor("productName", {
      id: "productName",
      header: "Product",
      size: 200,
    }),
    deliveryLineHelper.accessor("qty", {
      id: "qty",
      header: "Qty",
      size: 88,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    deliveryLineHelper.accessor("lotCode", {
      id: "lotCode",
      header: "Lot",
      size: 120,
      cell: (info) => info.getValue() ?? "—",
    }),
    deliveryLineHelper.accessor("serialCode", {
      id: "serialCode",
      header: "Serial",
      size: 120,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}

export type PaymentAllocationRow = {
  id: string;
  salesOrderId: string | null;
  salesInvoiceId?: string | null;
  soNumber: string | null;
  siNumber?: string | null;
  amount: string;
};

const allocHelper =
  createColumnHelper<DeskTableFeatures, PaymentAllocationRow>();

export function customerPaymentAllocationColumns(companySlug: string) {
  return allocHelper.columns([
    allocHelper.display({
      id: "appliedTo",
      header: "Applied to",
      size: 200,
      cell: (info) => {
        const row = info.row.original;
        if (row.salesInvoiceId && row.siNumber) {
          return (
            <DocNumberLink
              to={`/c/${companySlug}/sales/invoices/${row.salesInvoiceId}`}
            >
              {row.siNumber}
            </DocNumberLink>
          );
        }
        if (row.salesOrderId && row.soNumber) {
          return (
            <DocNumberLink
              to={`/c/${companySlug}/sales/orders/${row.salesOrderId}`}
            >
              {row.soNumber}
            </DocNumberLink>
          );
        }
        return "—";
      },
    }),
    allocHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
  ]);
}
